// Cloche d'alertes du bandeau : compteur de non-acquittées + panneau déroulant.
//
// Le compteur ne compte QUE les alertes actives et non acquittées. Quand tout
// va bien, la cloche est grise et sans pastille : le silence doit signifier
// « normal », sinon l'indicateur ne veut plus rien dire au bout d'une semaine.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { alertsApi, alertTitle, METRIC_LABELS, type AlertEvent } from '../api/alerts';
import { displaySn } from '../utils/deviceDisplay';
import { useI18n } from '../i18n';

type TFn = ReturnType<typeof useI18n>['t'];

const POLL_MS = 60_000;

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-rose-500',
  warning: 'bg-amber-500',
  info: 'bg-sky-500',
};

/** « il y a 12 min », « depuis 3 h » — la durée compte autant que le fait. */
function since(iso: string, t: TFn): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return t('team_bell_justNow');
  const min = Math.floor(diff / 60_000);
  if (min < 60) return t('eq2_dur_min', { m: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('eq2_dur_h', { h });
  return t('team_bell_days', { n: Math.floor(h / 24) });
}

function formatValue(metric: string, value: number | null): string {
  if (value === null) return '—';
  const unit = METRIC_LABELS[metric]?.unit ?? '';
  const decimals = metric === 'power_factor' ? 3 : 1;
  return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
}

export function AlertBell() {
  const { t, tp } = useI18n();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await alertsApi.list(24);
      if (!mountedRef.current) return;
      setAlerts(res.data ?? []);
      setUnread(res.unread ?? 0);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      // Une erreur ici ne doit pas polluer le bandeau : on la garde pour le panneau.
      setError((err as Error).message);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    const id = setInterval(load, POLL_MS);
    return () => { mountedRef.current = false; clearInterval(id); };
  }, [load]);

  // Fermeture au clic extérieur
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const acknowledge = async (id: number) => {
    // Optimiste : l'accusé de lecture est sans risque, on n'attend pas le serveur.
    setAlerts(prev => prev.map(a => (a.id === id ? { ...a, acknowledgedAt: new Date().toISOString() } : a)));
    setUnread(prev => Math.max(0, prev - 1));
    try {
      await alertsApi.acknowledge(id);
    } catch {
      load(); // échec : on resynchronise sur la vérité du serveur
    }
  };

  const active = alerts.filter(a => a.isActive);
  const resolved = alerts.filter(a => !a.isActive);

  // ── Deux signaux distincts ────────────────────────────────────────────────
  // • badge chiffré  = alertes NON ACQUITTÉES → « ça demande ton attention »
  // • cloche colorée = alertes ACTIVES        → « quelque chose ne va toujours pas »
  // Acquitter éteint le badge, mais PAS la cloche : sinon l'interface paraîtrait
  // calme alors que le problème dure encore.
  const hasActive = active.length > 0;
  const bellTone = unread > 0
    ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-50'
    : hasActive
      ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100';

  const bellTitle = unread > 0
    ? tp('team_bell_unread', unread)
    : hasActive
      ? tp('team_bell_stillActive', active.length)
      : t('team_bell_none');

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`relative p-2 rounded-md transition-colors ${bellTone}`}
        title={bellTitle}
        aria-label={t('team_bell_alerts')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1h6z" />
        </svg>
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : hasActive ? (
          // Pastille sans chiffre : acquittée, mais toujours en cours.
          <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white" />
        ) : null}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
          <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 text-sm">{t('team_bell_alerts')}</h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {tp('team_bell_activeCount', active.length)}
              </span>
              <Link
                to="/alertes/seuils"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold text-primary-700 hover:text-primary-900"
              >
                {t('team_bell_setThresholds')}
              </Link>
            </div>
          </div>

          {error && (
            <p className="px-4 py-3 text-xs text-rose-600 bg-rose-50">{error}</p>
          )}

          {active.length === 0 && resolved.length === 0 && !error && (
            <div className="px-4 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">{t('team_bell_none')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('team_bell_allGood')}</p>
            </div>
          )}

          {active.map(a => (
            <div key={a.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
              <div className="flex items-start gap-2.5">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[a.severity] ?? 'bg-gray-400'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800">{alertTitle(a, t)}</p>
                  <p className="text-xs text-gray-500 truncate">{displaySn(a.deviceSn) || a.deviceSn}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {formatValue(a.metric, a.valuePeak ?? a.valueTriggered)}
                    <span className="text-gray-400">
                      {' '}— {t('team_bell_threshold')} {a.operator === 'gt' ? '>' : '<'} {formatValue(a.metric, a.threshold)}
                    </span>
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-gray-400">{t('team_bell_since', { time: since(a.triggeredAt, t) })}</span>
                    {a.acknowledgedAt ? (
                      // « Acquittée » seul se lirait comme « réglée » : on rappelle
                      // que la condition dure toujours.
                      <span className="text-[11px] text-amber-600 font-medium">
                        {t('team_bell_ackOngoing')}
                      </span>
                    ) : (
                      <button
                        onClick={() => acknowledge(a.id)}
                        className="text-[11px] font-semibold text-primary-700 hover:text-primary-900 px-2 py-0.5 rounded hover:bg-primary-50 transition-colors"
                      >
                        {t('team_bell_acknowledge')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {resolved.length > 0 && (
            <>
              <p className="px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50">
                {t('team_bell_resolved')}
              </p>
              {resolved.map(a => (
                <div key={a.id} className="px-4 py-2 border-b border-gray-50 flex items-center gap-2.5">
                  <span className="text-emerald-500 text-xs">✓</span>
                  <p className="text-xs text-gray-500 truncate flex-1">
                    {alertTitle(a, t)} · {displaySn(a.deviceSn) || a.deviceSn}
                  </p>
                  <span className="text-[11px] text-gray-400 flex-shrink-0">{since(a.triggeredAt, t)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AlertBell;
